import { Box } from "@mui/material";
import moment from "moment";
import ContentNormal from "components/elements/ContentNormal";
import ContentSmall from "components/elements/ContentSmall";
import ClipboardCheck from "components/icons/ClipboardCheck";
import ClipboardCross from "components/icons/ClipboardCross";
import ShippingTruck from "components/icons/ShippingTruck";
import ShippingTruckCheck from "components/icons/ShippingTruckCheck";
import { AppContext } from "contexts/AppContext";
import { useContext } from "react";
import { translate } from "utils/Translate";
import ContentExtraSmall from "components/elements/ContentExtraSmall";

function OrderTrackingStatus(props) {
  const { orderFlowStatus, trackingEvents, trackingUrl, trackingNumber } = props;
  const { styleGuide } = useContext(AppContext);

  const isNew = orderFlowStatus === 'NEW';
  const isProcessed = ['BACKORDER', 'TENTATIVE', 'ORDERED', 'PACKED'].includes(orderFlowStatus);
  const isSent = orderFlowStatus === 'SHIPPED';
  const isDelivered = orderFlowStatus === 'RECEIVED';
  const isRefunded = orderFlowStatus === 'REFUNDED';
  const isFailed = !isNew && !isProcessed && !isSent && !isDelivered && !isRefunded;

  const trackingLink = trackingNumber && trackingUrl ? `${trackingUrl}${trackingNumber}` : null;

  return (
    <Box sx={{ border: '1px solid', borderColor: styleGuide.color2, borderRadius: 4, p: {xs: 2, sm: 4} }}>
      {(isRefunded || isFailed) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          <Box sx={{ width: 100 }}>
            <ClipboardCross />
          </Box>

          {isRefunded && (
            <ContentNormal
              translationKey="_ORDER_TRACKING_REFUNDED_"
              fallbackText="Denne del af din ordre er refunderet."
            />
          )}
          
          {isFailed && (
            <ContentNormal
              translationKey="_ORDER_TRACKING_FAILED_"
              fallbackText="Der opstod en fejl med din ordre."
            />
          )}
        </Box>
      )}

      {!isRefunded && !isFailed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', borderBottom: '1px solid', borderBottomColor: styleGuide.color2 }}>
            <IconWrapper completed={isProcessed}>
              <ClipboardCheck />
            </IconWrapper>
            <IconWrapper completed={isSent}>
              <ShippingTruck />
            </IconWrapper>
            <IconWrapper completed={isDelivered}>
              <ShippingTruckCheck/>
            </IconWrapper>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isNew && 
              <ContentNormal
                translationKey="_ORDER_TRACKING_PROCESSING_"
                fallbackText="Din ordre er modtaget og behandles snarest."
              />
            }
            {isProcessed && 
              <ContentNormal
                translationKey="_ORDER_TRACKING_PROCESSED_"
                fallbackText="Denne del af din ordre er under behandling. Du vil modtage en besked, når den er afsendt."
              />
            }
            {isSent &&
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {!!trackingNumber
                  ? 
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ContentNormal
                        translationKey="_ORDER_TRACKING_TRACKING_"
                        fallbackText="Denne del af din ordre er afsendt. Du kan følge forsendelsen her:"
                      />
                      <TrackingEvents events={trackingEvents} styleGuide={styleGuide} />

                      {trackingLink && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ContentSmall>
                            {translate('_ORDER_TRACKING_TRACKING_NUMBER_', 'Trackingnummer')}: <a 
                              href={`${trackingLink}`} target='_blank' rel='noreferrer' style={{ textDecoration: 'none', color: styleGuide.color16 }}
                            >
                              {trackingNumber}
                            </a>
                          </ContentSmall>
                        </Box>
                      )}
                    </Box>
                  :
                    <ContentNormal
                      translationKey="_ORDER_TRACKING_DROPSHIP_"
                      fallbackText="Dropship levering. Produkterne er bestilt hos vores leverandør. Du vil modtage tracking-oplysninger direkte fra leverandøren så snart forsendelsen er afsendt."
                    />
                }
              </Box>
            }
            {isDelivered &&
              <ContentNormal
                translationKey="_ORDER_TRACKING_DELIVERED_"
                fallbackText="Denne del af din ordre er færdigbehandlet og leveret."
              />
            }
          </Box>
        </Box>
      )}
    </Box>
  );
}

function IconWrapper({ completed,children }) {
  return (
    <Box sx={{ width: 70, opacity: completed ? 1 : 0.2 }}>
      {children}
    </Box>
  );
}

function TrackingEvents({ events, styleGuide }) {
  return (
    <Box>
      {events.map((event, index) => (
        <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }} style={{
          borderLeft: '2px solid',
          borderLeftColor: styleGuide.color4,
          width: '100%',
          position: 'relative',
          paddingTop: 30,
          paddingLeft: 15
        }}>

          {/* Green circle */}
          <div style={{ width: 8, height: 8, borderRadius: 50, backgroundColor: styleGuide.color16, position: 'absolute', left: -5, top: 38 }} />

          <ContentSmall style={{ fontWeight: 'bold' }}>
            {event.description}
          </ContentSmall>
          <ContentSmall>
            {event.location}
          </ContentSmall>
          <ContentExtraSmall style={{ color: styleGuide.color5 }}>
            {moment(event.timestamp).format('DD/MM/YYYY, HH:mm')}
          </ContentExtraSmall>
        </Box>
      ))}
    </Box>
  );
}

export default OrderTrackingStatus;